import { graphql } from 'gql.tada';
import { client } from '../client';
import type { OrderTokenType } from './getRetailerSettings';
import { pdxEvent } from '../../utils/noflake';

export const getOrderQuery = graphql(`
  query GetOrder($input: OrderInput!) {
    order(input: $input) {
      shippingAddress {
        postalCode
      }
      orderId
      items {
        id
        name
        title
        sku
        quantity {
          ordered
          pending
          completed
          remaining
        }
        reasons {
          code
          retailerReasonCode
          retailerReasonCodeId
          resolutions {
            id
            scope
            label
          }
          fields {
            id
            type
            required
            max
          }
          parentId
        }
        price {
          value
          formattedString
          currency
        }
        image {
          url
        }
        variant {
          id
          info {
            name
            value
          }
        }
        ineligibleReasons
        eligibility {
          eligibleUntil
          returnWindowClosedOn
        }
        claims {
          claimId
          claimNumber
          claimItemId
          status
          reasonId
          submittedAt
          quantity
        }
      }
      customer {
        id
        firstName
        lastName
      }
      orderNumber
      authorization {
        token
      }
      orderTokens {
        key
        value
      }
      shipments {
        carrier
        carrierService
        itemsInfo {
          itemId
          quantity
          sku
        }
        shipDate
        shipMethod
        shippedTo {
          address {
            address1
            address2
            city
            province
            provinceCode
            countryCode
            postalCode
            firstName
            lastName
          }
          customer {
            id
            firstName
            lastName
            email
            phone
          }
        }
        status
        trackingNumber
      }
    }
    submittedRequests(input: $input) {
      id
      cancellable
      status
      packagingOption
      deliveryType
      deliveryMethod
      location {
        storeName
      }
      submitDate
      shipByDate
      items {
        id
        name
        quantity
        resolution
        refundPrice
        returnByDate
        image {
          url
        }
        reason {
          code
          retailerReasonCode
          retailerReasonCodeId
          resolutions {
            id
            scope
            label
          }
        }
      }
      refund {
        type
        estimateBreakdown {
          label
          formattedDisplay
          type
        }
      }
      shipments {
        trackingNumber
        trackingUrl
        labelUrl
        qrCodeUrl
      }
    }
  }
`);

export const getOrderWithoutEligibilityQuery = graphql(`
  query GetOrder($input: OrderInput!) {
    order(input: $input) {
      shippingAddress {
        postalCode
      }
      orderId
      items {
        id
        name
        title
        sku
        quantity {
          ordered
          pending
          completed
          remaining
        }
        price {
          value
          formattedString
          currency
        }
        image {
          url
        }
        variant {
          id
          info {
            name
            value
          }
        }
      }
      customer {
        id
        firstName
        lastName
      }
      orderNumber
      authorization {
        token
      }
      orderTokens {
        key
        value
      }
      shipments {
        carrier
        carrierService
        itemsInfo {
          itemId
          quantity
          sku
        }
        shipDate
        shipMethod
        shippedTo {
          address {
            address1
            address2
            city
            province
            provinceCode
            countryCode
            postalCode
            firstName
            lastName
          }
          customer {
            id
            firstName
            lastName
            email
            phone
          }
        }
        status
        trackingNumber
      }
    }
    submittedRequests(input: $input) {
      id
      cancellable
      status
      packagingOption
      deliveryType
      deliveryMethod
      location {
        storeName
      }
      submitDate
      shipByDate
      items {
        id
        name
        quantity
        resolution
        refundPrice
        returnByDate
        image {
          url
        }
        reason {
          code
          retailerReasonCode
          retailerReasonCodeId
          resolutions {
            id
            scope
            label
          }
        }
      }
      refund {
        type
        estimateBreakdown {
          label
          formattedDisplay
          type
        }
      }
      shipments {
        trackingNumber
        trackingUrl
        labelUrl
        qrCodeUrl
      }
    }
  }
`);

export type OrderToken = {
  key: OrderTokenType;
  value: string;
};

export const getOrderQueryFn = async ({
  input,
  skipEligibility = false,
}: {
  input: GetOrderVariables['input'];
  skipEligibility?: boolean;
}) => {
  pdxEvent({
    event_category: 'get_data',
    event_type: 'get_order',
    event_value: 'order',
  });

  try {
    const result = await client.request(
      skipEligibility ? getOrderWithoutEligibilityQuery : getOrderQuery,
      { input },
    );

    pdxEvent({
      event_category: 'get_data',
      event_type: 'get_order_success',
      event_value: 'order',
    });

    return result;
  } catch (error) {
    pdxEvent({
      event_category: 'error',
      event_type: 'get_order_error',
      event_value: 'get_data failed to get order',
    });
    throw error;
  }
};

/* eslint-disable typescript/no-explicit-any -- Required for gql.tada type inference */
export type GetOrderVariables =
  typeof getOrderQuery extends import('gql.tada').TadaDocumentNode<
    any,
    infer T,
    any
  >
    ? T
    : never;
/* eslint-enable typescript/no-explicit-any */

export type GetOrderResult = Awaited<ReturnType<typeof getOrderQueryFn>>;
export type Order = GetOrderResult['order'];
export type Customer = GetOrderResult['order']['customer'];
export type OrderItems = GetOrderResult['order']['items'];
export type OrderItem = OrderItems[number];
export type Reasons = NonNullable<OrderItems[number]['reasons']>;
export type Reason = Reasons[number];
export type Claims = NonNullable<OrderItem['claims']>;
export type Claim = Claims[number];
export type OrderTokens = GetOrderResult['order']['orderTokens'];
export type SubmittedRequest = GetOrderResult['submittedRequests'][number];
export type SubmittedRequestItem = SubmittedRequest['items'][number];
export type Shipments = NonNullable<Order['shipments']>;
export type Shipment = Shipments[number];
export type ShipmentStatus = NonNullable<Shipment['status']>;
